/**
 * ResetAllToDefaultDialog — confirm + result for the bulk "Reset to defaults".
 *
 * The confirm step renders a PREVIEW the server produced with the same planner
 * the write uses (POST /reset-all-to-default with dry_run:true), so the list an
 * operator approves is the list that will be applied — not a client-side guess
 * that can drift from it.
 *
 * Two things this dialog must never do:
 *   1. Say "all". Fixed rows and the two safety-path pipelines are excluded by
 *      the server, so a bulk reset is never "everything". Both the preview and
 *      the result name the count that moved AND the count that did not.
 *   2. Hide a skip. Every untouched row is listed under its own reason,
 *      including the two safety pipelines and why they are handled alone.
 *
 * Visual language follows the page: QuillActionConfirm for the production
 * confirm, a plain dialog for the outcome report.
 */

import React from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { colors, typography } from '../../../theme/designTokens';
import {
  PipelineResetResponse,
  PipelineResetResultRow,
  PipelineResetOutcome,
  PipelineResetSummary,
} from '../../../services/quillAdminService';
import QuillActionConfirm from '../common/QuillActionConfirm';

/** Section headings, in the order an operator reads them. */
const SECTIONS: { outcome: PipelineResetOutcome; heading: (n: number) => string; note?: string }[] = [
  {
    outcome: 'changed',
    heading: (n) => `Will be reset to its default (${n})`,
  },
  {
    outcome: 'failed',
    heading: (n) => `Cannot be reset (${n})`,
    note: 'The stored default is no longer a model these pipelines can use. Nothing is written for these.',
  },
  {
    outcome: 'skipped_safety_path',
    heading: (n) => `Not included — safety path (${n})`,
    note:
      'These two decide what a member is allowed to be told, and what they hear when something is '
      + 'blocked. Changing the model behind either one takes its own confirmation, its own safety '
      + 're-verification, and a live test of the new model — so a single click can never move them. '
      + 'Change them one at a time from their own rows.',
  },
  {
    outcome: 'skipped_immutable',
    heading: (n) => `Not included — fixed infrastructure (${n})`,
    note: 'Their model is welded to stored data or to a code constant and is never admin-changeable.',
  },
  {
    outcome: 'skipped_no_default',
    heading: (n) => `Not included — no default recorded (${n})`,
  },
  {
    outcome: 'already_on_default',
    heading: (n) => `Already on its default (${n})`,
    note: 'Nothing is written for these.',
  },
];

const DONE_HEADING: Partial<Record<PipelineResetOutcome, (n: number) => string>> = {
  changed: (n) => `Reset to its default (${n})`,
  failed: (n) => `Could not be reset (${n})`,
};

/**
 * The honest headline. Never the word "all" — fixed rows and the two safety
 * pipelines are always excluded, so "all" could not be true.
 */
export function resetHeadline(summary: PipelineResetSummary, done: boolean): string {
  const untouched = summary.total - summary.changed;
  if (done) {
    if (summary.changed === 0) {
      return `No pipeline was changed. All ${summary.total} were left as they were.`;
    }
    return (
      `Reset ${summary.changed} of ${summary.total} pipelines to their default. `
      + `${untouched} ${untouched === 1 ? 'was' : 'were'} not touched.`
    );
  }
  if (summary.changed === 0) {
    return `Nothing would change. All ${summary.total} pipelines are already where they belong or are excluded.`;
  }
  return (
    `${summary.changed} of ${summary.total} pipelines would be reset to their default. `
    + `${untouched} would not be touched.`
  );
}

const rowsFor = (results: PipelineResetResultRow[], outcome: PipelineResetOutcome) =>
  results.filter((r) => r.outcome === outcome);

const RowLine: React.FC<{ row: PipelineResetResultRow }> = ({ row }) => (
  <Box component="li" sx={{ mb: 0.5 }} data-testid={`reset-row-${row.pipeline_key}`}>
    <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
      {row.display_name}
    </Typography>{' '}
    <Typography
      variant="caption"
      component="span"
      sx={{ fontFamily: typography.fonts.mono, color: 'text.disabled' }}
    >
      {row.pipeline_key}
    </Typography>
    {row.from && row.to && (
      <Typography
        variant="body2"
        sx={{ fontFamily: typography.fonts.mono, color: colors.gray[700] }}
      >
        {row.from.provider} · {row.from.model_id} → {row.to.provider} · {row.to.model_id}
      </Typography>
    )}
    {row.reason && (
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
        {row.reason}
      </Typography>
    )}
    {row.change_log_failed && (
      <Typography variant="caption" sx={{ display: 'block', color: colors.warning.dark }}>
        The model moved, but its audit row was not written.
      </Typography>
    )}
  </Box>
);

/** The grouped, per-row plan/report. Shared by the confirm and result states. */
export const ResetOutcomeList: React.FC<{ response: PipelineResetResponse; done: boolean }> = ({
  response,
  done,
}) => (
  <Box>
    {SECTIONS.map((section) => {
      const rows = rowsFor(response.results, section.outcome);
      if (rows.length === 0) return null;
      const heading = done && DONE_HEADING[section.outcome]
        ? (DONE_HEADING[section.outcome] as (n: number) => string)(rows.length)
        : section.heading(rows.length);
      return (
        <Box key={section.outcome} sx={{ mb: 1.5 }} data-testid={`reset-section-${section.outcome}`}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {heading}
          </Typography>
          {section.note && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
              {section.note}
            </Typography>
          )}
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {rows.map((row) => (
              <RowLine key={row.pipeline_key} row={row} />
            ))}
          </Box>
        </Box>
      );
    })}
  </Box>
);

export interface ResetAllToDefaultDialogProps {
  open: boolean;
  previewLoading: boolean;
  preview: PipelineResetResponse | null;
  previewError: string | null;
  submitting: boolean;
  result: PipelineResetResponse | null;
  onConfirm: () => void;
  onClose: () => void;
}

export const ResetAllToDefaultDialog: React.FC<ResetAllToDefaultDialogProps> = ({
  open,
  previewLoading,
  preview,
  previewError,
  submitting,
  result,
  onConfirm,
  onClose,
}) => {
  // ── Result state ──────────────────────────────────────────────────────
  if (result) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: typography.fonts.display }}>
          Reset to defaults — what happened
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1.5 }} data-testid="reset-result-headline">
            {resetHeadline(result.summary, true)}
          </Typography>
          <ResetOutcomeList response={result} done />
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1 }}>
            {result.invalidation === 'confirmed'
              ? 'The engine confirmed it dropped its model cache, so the changes are live now.'
              : result.invalidation === 'ttl_fallback'
                ? 'The engine did not confirm the cache drop — it will pick the changes up within about 60 seconds.'
                : 'No model changed, so there was no cache to clear.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ── Preview loading / error ───────────────────────────────────────────
  if (open && (previewLoading || previewError || !preview)) {
    return (
      <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: typography.fonts.display }}>Reset to defaults</DialogTitle>
        <DialogContent dividers>
          {previewError ? (
            <Alert severity="error" data-testid="reset-preview-error">
              {previewError}
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Working out exactly what would change…</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ── Nothing would change ──────────────────────────────────────────────
  // Do not offer a confirm button for a write with no rows. The operator still
  // sees the full picture — including why each row is excluded — and leaves
  // without touching production.
  if (preview && preview.summary.changed === 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: typography.fonts.display }}>
          Reset to defaults — nothing would change
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1.5 }} data-testid="reset-preview-headline">
            {resetHeadline(preview.summary, false)}
          </Typography>
          <ResetOutcomeList response={preview} done={false} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ── Confirm state ─────────────────────────────────────────────────────
  return (
    <QuillActionConfirm
      open={open && !!preview}
      title="Reset pipelines to their defaults"
      message={
        <Box component="span" data-testid="reset-preview-headline">
          {preview ? resetHeadline(preview.summary, false) : ''}
        </Box>
      }
      detail={preview ? <ResetOutcomeList response={preview} done={false} /> : undefined}
      caption="This writes to the production Quill DB and affects real members. The engine picks the changes up within ~60 seconds."
      confirmLabel={
        preview
          ? `Reset ${preview.summary.changed} ${preview.summary.changed === 1 ? 'pipeline' : 'pipelines'}`
          : 'Confirm'
      }
      destructive
      loading={submitting}
      pendingLabel="Resetting…"
      onConfirm={onConfirm}
      onCancel={onClose}
    />
  );
};

export default ResetAllToDefaultDialog;
